import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import NavigationTitle from '../global/NavigationTitle';
import { RootState } from '../../store/reducers';
import { USE_LITE_DRAFT_SAVE } from '../../constants/featureFlags';

type Props = {
  siteName: string | { en: string; id: string };
  style?: object;
};

const STATUS_COLORS: Record<string, string> = {
  saving: 'rgba(255,255,255,0.85)',
  saved: '#FFFFFF',
  offline: '#FB6A4F',
  error: '#FB6A4F',
  dirty: '#FB6A4F'
};

const SaveStatusText = () => {
  const { t } = useTranslation(['questionnaire']);
  const autoSaveStatus = useSelector((state: RootState) => state.draftSaveStatus.autoSaveStatus);
  const manualSyncStatus = useSelector((state: RootState) => state.draftSaveStatus.manualSyncStatus);

  // Priority: manualSyncStatus > autoSaveStatus > default "Unsaved"
  // Only manual save shows "Saved" (green). Default state is always "Unsaved" (red).
  let label: string;
  let color: string;

  if (manualSyncStatus === 'saving') {
    label = t('questionnaire:saving');
    color = STATUS_COLORS.saving;
  } else if (manualSyncStatus === 'saved') {
    label = t('questionnaire:saved');
    color = STATUS_COLORS.saved;
  } else if (manualSyncStatus === 'error') {
    label = t('questionnaire:unsaved');
    color = STATUS_COLORS.error;
  } else if (manualSyncStatus === 'offline') {
    // Offline saves are stored locally, but still need a remote sync.
    label = t('questionnaire:unsaved');
    color = STATUS_COLORS.offline;
  } else if (autoSaveStatus === 'saving') {
    label = t('questionnaire:saving');
    color = STATUS_COLORS.saving;
  } else if (autoSaveStatus === 'error') {
    label = t('questionnaire:unsaved');
    color = STATUS_COLORS.error;
  } else if (autoSaveStatus === 'offline') {
    label = t('questionnaire:unsaved');
    color = STATUS_COLORS.offline;
  } else if (autoSaveStatus === 'saved') {
    // Auto-save succeeded — reflect "Saved" without requiring a manual save.
    label = t('questionnaire:saved');
    color = STATUS_COLORS.saved;
  } else {
    // Default: show "Unsaved" until a save succeeds
    label = t('questionnaire:unsaved');
    color = STATUS_COLORS.dirty;
  }

  return <Text style={[styles.statusText, { color }]}>{label}</Text>;
};

const QuestionnaireHeaderTitle = ({ siteName, style }: Props) => {
  return (
    <View style={styles.container}>
      <NavigationTitle style={[styles.title, style]} defaultTintColor="white" title={siteName} />
      {USE_LITE_DRAFT_SAVE && <SaveStatusText />}
    </View>
  );
};

export default QuestionnaireHeaderTitle;

const styles = StyleSheet.create({
  container: {
    alignItems: 'center'
  },
  title: {
    fontSize: 18,
    marginHorizontal: 16
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 5
  }
});
